import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Permission constants that exactly match what GET /api/config/user-permissions returns.
 *
 * Role→permission mapping (resolved server-side from Entra group membership):
 *   CS-ControlCenterAdmin → all permissions + CCAdmin_Permissions
 *   CS-TeleMarketer       → Marketing_View, Marketing_Manage, Telemarketer_Permissions
 *   CS-Marketer           → Marketing_View, Marketer_Permissions
 */
export const CC_PERMISSIONS = {
    // Marketing
    MARKETING_VIEW: 'Marketing_View',
    MARKETING_MANAGE: 'Marketing_Manage',
    // Admin
    ADMIN_CREATE: 'Admin_Create',
    ADMIN_MANAGE: 'Admin_Manage',
    // Agents
    AGENT_MANAGE: 'Agent_Manage',
    AGENT_CREATE: 'Agent_Create',
    AGENT_LEAVE_MANAGE: 'AgentLeave_Manage',
    AGENT_LEAVE_APPLY: 'AgentLeave_Apply',
    // Scrap Collections
    SCRAP_COLLECTION_MANAGE: 'ScrapCollection_Manage',
    SCRAP_COLLECTION_CREATE: 'ScrapCollection_Create',
    SCRAP_COLLECTION_LIST: 'ScrapCollection_List',
    // Scrap Items
    SCRAP_ITEM_MANAGE: 'ScrapItem_Manage',
    SCRAP_ITEM_LIST: 'ScrapItem_List',
    // Banners
    BANNER_MANAGE: 'Banner_Manage',
    BANNER_CREATE: 'Banner_Create',
    BANNER_VIEW: 'Banner_View',
    BANNER_LIST: 'Banner_List',
    BANNER_DELETE: 'Banner_Delete',
    // GeoZones
    GEOZONE_MANAGE: 'GeoZone_Manage',
    GEOZONE_CREATE: 'GeoZone_Create',
    GEOZONE_LIST: 'GeoZone_List',
    // Districts
    DISTRICT_MANAGE: 'District_Manage',
    DISTRICT_CREATE: 'District_Create',
    DISTRICT_LIST: 'District_List',
    // Partners
    PARTNER_MANAGE: 'Partner_Manage',
    PARTNER_CREATE: 'Partner_Create',
    PARTNER_SEARCH_CUSTOMERS: 'Partner_SearchCustomers',
    PARTNER_VIEW_VOUCHERS: 'Partner_ViewVouchers',
    PARTNER_VIEW_VOUCHERS_OUTLET: 'Partner_ViewVouchersOutlet',
    PARTNER_VIEW_INVOICES: 'Partner_ViewInvoices',
    // Vouchers
    VOUCHER_MANAGE: 'Voucher_Manage',
    VOUCHER_CONFIG: 'VoucherConfig_Manage',
    // Holidays
    HOLIDAY_MANAGE: 'Holiday_Manage',
    HOLIDAY_CREATE: 'Holiday_Create',
    HOLIDAY_LIST: 'Holiday_List',
    // Appointments
    APPOINTMENTS_MANAGE: 'Appointments_Manage',
    APPOINTMENT_CREATE: 'Appointment_Create',
    APPOINTMENT_LIST: 'Appointment_List',
    // Customers
    CUSTOMER_MANAGE: 'Customer_Manage',
    // Subscriptions
    SUBSCRIPTION_MANAGE: 'Subscription_Manage',
    // RazorPay / Shopify
    RAZORPAY: 'RazorPay',
    SHOPIFY_LIST: 'Shopify_List',
    // Reports
    REPORT_VIEW: 'Report_View',
    // ─── Composite role sentinels (reliable role signal) ───
    IS_CC_ADMIN: 'CCAdmin_Permissions',
    IS_TELEMARKETER: 'Telemarketer_Permissions',
    IS_MARKETER: 'Marketer_Permissions',
} as const;

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly apiUrl = `${environment.apiConfig.uri}/api/config/user-permissions`;

    /** Internal permission set — populated once after login via loadPermissions() */
    private permissions = new Set<string>();

    /** Emits true once permissions have been loaded (even if empty on failure) */
    readonly loaded$ = new BehaviorSubject<boolean>(false);

    constructor(private http: HttpClient) { }

    // ─────────────────────────────────────────────────────────────────────────
    // Bootstrap
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Load permissions from the API.
     * Call this ONCE after MSAL has acquired a token.
     * Returns a Promise so it can be used inside APP_INITIALIZER.
     */
    async loadPermissions(): Promise<void> {
        try {
            const perms = await firstValueFrom(
                this.http.get<string[]>(this.apiUrl)
            );
            this.permissions = new Set(perms ?? []);
        } catch (err) {
            console.error('[PermissionService] Failed to load permissions:', err);
            this.permissions = new Set();
        }

        this.loaded$.next(true);
    }

    /**
     * Clear all permissions — call on logout.
     */
    clearPermissions(): void {
        this.permissions = new Set();
        this.loaded$.next(false);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Core checks
    // ─────────────────────────────────────────────────────────────────────────

    /** Returns true if the current user has the given permission string. */
    has(permission: string): boolean {
        return this.permissions.has(permission);
    }

    /** Returns true if the current user has at least one of the given permissions. */
    hasAny(...permissions: string[]): boolean {
        return permissions.some(p => this.permissions.has(p));
    }

    /** Returns true if the current user has ALL of the given permissions. */
    hasAll(...permissions: string[]): boolean {
        return permissions.every(p => this.permissions.has(p));
    }

    /** Returns true if permissions have been loaded and the set is non-empty. */
    isPermissionsLoaded(): boolean {
        return this.loaded$.value;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Role helpers
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * CC Admin — group CS-ControlCenterAdmin.
     * Reliable signal: CCAdmin_Permissions is only present for CC Admins.
     */
    isCCAdmin(): boolean {
        return this.has(CC_PERMISSIONS.IS_CC_ADMIN);
    }

    /**
     * Marketer — group CS-Marketer, NOT a CC Admin.
     */
    isMarketer(): boolean {
        return this.has(CC_PERMISSIONS.IS_MARKETER) && !this.isCCAdmin();
    }

    /**
     * Telemarketer — group CS-TeleMarketer, NOT a CC Admin.
     */
    isTelemarketer(): boolean {
        return this.has(CC_PERMISSIONS.IS_TELEMARKETER) && !this.isCCAdmin();
    }

    /**
     * Any marketing role — Marketer, Telemarketer, or CC Admin.
     *
     * Checks BOTH the granular permission (Marketing_View) AND the role
     * sentinels (Marketer_Permissions, Telemarketer_Permissions) because the
     * backend may return either form depending on the rbac.config.json setup.
     * If the backend expands composite permissions, Marketing_View will be
     * present. If it returns only the sentinel, the sentinel check catches it.
     */
    hasMarketingAccess(): boolean {
        return this.hasAny(
            CC_PERMISSIONS.MARKETING_VIEW,       // returned when backend expands composites
            CC_PERMISSIONS.IS_MARKETER,          // Marketer_Permissions sentinel
            CC_PERMISSIONS.IS_TELEMARKETER       // Telemarketer_Permissions sentinel
        );
    }

    /**
     * Can write/manage marketing data — Telemarketer or CC Admin.
     *
     * Checks both the granular permission (Marketing_Manage) and the
     * Telemarketer sentinel in case the backend returns only the composite.
     */
    canWriteMarketing(): boolean {
        return this.hasAny(
            CC_PERMISSIONS.MARKETING_MANAGE,     // returned when backend expands composites
            CC_PERMISSIONS.IS_TELEMARKETER       // Telemarketer_Permissions sentinel
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Display helpers
    // ─────────────────────────────────────────────────────────────────────────

    /** Human-friendly role label for UI display. */
    getRoleDisplayName(): string {
        if (this.isCCAdmin()) return 'Control Center Admin';
        if (this.isTelemarketer()) return 'Telemarketer';
        if (this.isMarketer()) return 'Marketer';
        if (this.permissions.size === 0 && this.loaded$.value) return 'No Access';
        return 'User';
    }

    /** Tailwind badge colour class for the role pill in the header. */
    getRoleBadgeClass(): string {
        if (this.isCCAdmin()) return 'bg-green-100 text-green-700';
        if (this.isTelemarketer()) return 'bg-blue-100 text-blue-700';
        if (this.isMarketer()) return 'bg-purple-100 text-purple-700';
        return 'bg-gray-100 text-gray-500';
    }
}

